import { useLazyQuery, useMutation } from "@apollo/client";
import { Checkbox, CircularProgress, FormControlLabel, FormGroup, Grid, Typography, } from "@material-ui/core";
import { ApolloError } from "apollo-boost";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { loadStripe } from "@stripe/stripe-js";
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/Close';
import styles from './index.module.css';
import { IBooking, ICreditNote, PaymentGateway, PaymentMode } from "../../reducers/bookings/types";
import { useSnackBar } from "../SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../SnackbarWrapper/SnackbarWrapper";
import { IAppState } from "../../store";
import { CREATE_PAYMENT } from "../../graphql/invoices/createPaymentMutation";
import { formatGraphQLErrorMessage, toCurrency } from "../utils";
import Dialog from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import { CONFIRM_BOOKING } from '../../graphql/bookings/confirmBookingMutation';
import StripePayment from "./StripePayment";
import { RATE_TYPES } from '../views/utils';
import { DepositCollection, MinimumBookingAmountTypes } from "../consts";
import { CHECK_BOOKING_CONFIRMATION_ELIGIBILITY } from "../../graphql/bookings/checkBookingConfirmationEligibility";
import { loadConvergeUS } from "../../utils/converge";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ProcessingFeeType } from "../../reducers/website/types";
import { UserRole } from "../../reducers/auth/types";

export interface ICreditNotesToAdjust {
  creditNoteId: string;
  amount: number;
}

interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  booking: IBooking;
  onConfirmation: (bookingId: string) => void;
  returnUrl: string
  adjustableCreditNotes?: ICreditNote[]
}

const PaymentDialog: React.FC<IProps> = ({ booking, open, setOpen, onConfirmation, returnUrl, adjustableCreditNotes }) => {
  const snackbar = useSnackBar();
  const history = useHistory();
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const { user } = useSelector((state: IAppState) => state.authReducer);
  const [loading, setLoading] = useState<boolean>(false);
  const { currency, stripeAccountId, locale, autoChargeEnabled, convergeEnabled, enablePaymentProcessingFee, paymentProcessingFee } = website.organisation;
  const [paymentCollection, setPaymentCollection] = useState<boolean>(false);
  const [stripePaymentIntentSecret, setStripePaymentIntentSecret] = useState<string>("");
  const [saveDetails, setSaveDetails] = useState<boolean>(false);
  const [amountToPay, setAmountToPay] = useState<number>(0);
  const [collectDeposit, setCollectDeposit] = useState<boolean>(false);
  const [confirmDirectly, setConfirmDirectly] = useState(false);
  const [captureLater, setCaptureLater] = useState(false);
  const [creditNotesApplied, setCreditNotesApplied] = useState<boolean>(false);
  const [adjustableAmount, setAdjustableAmount] = useState<number>(0);
  const [creditNotesToAdjust, setCreditNotesToAdjust] = useState<ICreditNotesToAdjust[]>([]);
  const [bookingType, setBookingType] = useState<string>('PREPAID');
  const [paymentGatewayAvailable, setPaymentGatewayAvailable] = useState<boolean>(false);
  const [autoChargeSupported, setAutoChargeSupported] = useState<boolean>(false);
  const [processingFee, setProcessingFee] = useState<number>(0);
  const {t} = useTranslation();
  const showTextForConvergePayments =
    website.paymentGateway === PaymentGateway.CONVERGE
      ? website.organisation.address.country === "United States"
        ? true
        : false
      : true;
  
  const applyProcessingFee = (amount: number, paymentMode: PaymentMode) => {
    const paymentGateway = website.paymentGateway
    let currProcessingFee = 0;
    if(!enablePaymentProcessingFee) return 0;
    if (paymentProcessingFee) {
      const modeProcessingDetails = paymentProcessingFee.find((fee) => fee.paymentMode === paymentMode);
      if(modeProcessingDetails) {
        const processingFeeDetails = modeProcessingDetails?.gateways.find((gateway) => gateway.paymentGateway === paymentGateway);
        if (processingFeeDetails) {
          switch(processingFeeDetails.feeType) {
            case ProcessingFeeType.FIXED:
              currProcessingFee = processingFeeDetails.value;
              break;
            case ProcessingFeeType.PERCENTAGE:
              currProcessingFee = (amount * processingFeeDetails.value) / 100;
              break;
            default: 
              currProcessingFee = 0;
          }
        }
      }
    }
    return Math.round(currProcessingFee);
  }
  useEffect(() => {
    if (website && booking) {
      const selectedbranch = website.branches.find(branch => branch.id === booking.branchId);
      if (selectedbranch?.depositCollection === DepositCollection.AT_BOOKING_CONFIRMATION && booking?.depositDetails?.amount ) {
        setCollectDeposit(true);
      }
      const hasGlobalPaymentCredentials = Boolean(
        website.organisation?.globalPaymentCredentials?.gpAppId &&
          website.organisation?.globalPaymentCredentials?.gpAppKey
      );
      if (
        (website.paymentGateway === PaymentGateway.STRIPE && stripeAccountId) ||
        (website.paymentGateway === PaymentGateway.CONVERGE && convergeEnabled) ||
        (website.paymentGateway === PaymentGateway.GLOBAL_PAYMENT && hasGlobalPaymentCredentials)
      ) {
        setPaymentGatewayAvailable(true);
      }
      const currProcessingFee = applyProcessingFee(booking.paymentDetails.totalPayableAmount, PaymentMode.CARD)
      setProcessingFee(currProcessingFee);
    }
  }, [website, booking])

  useEffect(() => {
    if (booking) {
      setAmountToPay(getTotalPayableAmount())
    }
  }, [booking, collectDeposit, processingFee])

  useEffect(() => {
    if (adjustableCreditNotes?.length) {
      const amount = calcAdjustableAmount(adjustableCreditNotes);
      setAdjustableAmount(amount)
    }
  }, [adjustableCreditNotes])

  useEffect(() => {
    if (autoChargeEnabled) {
      if (website.paymentGateway === PaymentGateway.CONVERGE) {
        if (convergeEnabled && website.organisation.address.country === "United States") {
          setAutoChargeSupported(true)
        } else {
          setAutoChargeSupported(false)
        }
      } else {
        setAutoChargeSupported(true)
      }
    } else {
      setAutoChargeSupported(false)
    }
  }, [autoChargeEnabled])

  useEffect(() => {
    if (autoChargeSupported) {
      setSaveDetails(true)
    } else {
      setSaveDetails(false)
    }
  }, [autoChargeSupported])

  const handleDialogClose = () => {
    setOpen(false);
  };

  const [checkBookingConfirmationEligibility] = useLazyQuery(
    CHECK_BOOKING_CONFIRMATION_ELIGIBILITY,
    {
      fetchPolicy: "network-only",
      onError: (error: ApolloError) => {
        snackbar({
          message: formatGraphQLErrorMessage(error.message),
          variant: SnackBarVariant.ERROR
        });
        setLoading(false);
      },
      onCompleted: (data) => {
        if (data.bookingConfirmationEligibility) {
          if (booking) {
            if (confirmDirectly) {
              if (creditNotesToAdjust.length) {
                createPayment({ // signifies the case of pay on collection with some credit notes to adjust
                  variables: {
                    payment: {
                      bookingId: booking.id,
                      currency,
                      successUrl: `${window.location.protocol}//${window.location.host}/account/bookings/${booking.id}`,
                      cancelUrl: `${window.location.protocol}//${window.location.host}/account/bookings/${booking.id}`,
                    },
                    saveDetails,
                    collectDeposit: false, // because credit notes can't be adjusted against deposit
                    creditNotesToAdjust
                  }
                })
              } else {
                confirmBookingMutation({
                  variables: {
                    bookingId: booking.id,
                    branchId: booking.branchId,
                    bookingType
                  }
                })
              }
            } else {
              if (amountToPay) {
                createPayment({
                  variables: {
                    payment: {
                      bookingId: booking.id,
                      currency,
                      amount: amountToPay,
                      processingFee: enablePaymentProcessingFee ? processingFee: 0,
                      captureLater,
                      successUrl: `${window.location.protocol}//${window.location.host}/account/bookings/${booking.id}`,
                      cancelUrl: `${window.location.protocol}//${window.location.host}/account/bookings/${booking.id}`,
                    },
                    saveDetails,
                    collectDeposit,
                    creditNotesToAdjust
                  }
                });
              } else {
                createPayment({
                  variables: {
                    payment: {
                      bookingId: booking.id,
                      currency,
                      successUrl: `${window.location.protocol}//${window.location.host}/account/bookings/${booking.id}`,
                      cancelUrl: `${window.location.protocol}//${window.location.host}/account/bookings/${booking.id}`,
                    },
                    saveDetails,
                    collectDeposit: false, // because credit notes can't be adjusted against deposit
                    creditNotesToAdjust
                  }
                });
              }
            }
          }
        }
      }
    }
  );

  const [createPayment, { loading: sessionLoading, data: createPaymentData }] = useMutation(CREATE_PAYMENT, {
    onError: (error: ApolloError) => {
      // alert(error.message);
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    },
    onCompleted: async (data) => {
      if (data?.consumerCreatePayment.success && booking) {
        if (data?.consumerCreatePayment?.payment?.stripePaymentIntentSecret) {
          const stripePromise: any = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "", {
            stripeAccount: stripeAccountId
          });
          // Get Stripe.js instance
          const stripe = await stripePromise;
          // When the customer clicks on the button, redirect them to Checkout.
          if (stripe) {
            setLoading(false)
            setStripePaymentIntentSecret(data.consumerCreatePayment.payment.stripePaymentIntentSecret)
            setPaymentCollection(true);
          }
        } else if (data.consumerCreatePayment.payment?.convergePaymentSessionUrl) {
          window.location = data.consumerCreatePayment.payment.convergePaymentSessionUrl;
        } else if (data.consumerCreatePayment.payment?.convergeUSSessionToken) {
          loadConvergeUS({
            paymentSessionToken: data.consumerCreatePayment.payment?.convergeUSSessionToken,
            onloadFunction() {
              handleDialogClose();
            },
            redirectFunction() {
              history.push(`/account/bookings/${booking.id}?redirect_status=succeeded`);
            }
          });
        } else if (data.consumerCreatePayment.payment?.gpTransactionUrl) {
          window.location = data.consumerCreatePayment.payment.gpTransactionUrl;
        } else {
          onConfirmation(booking.id)
          handleDialogClose()
        }
      }
    }
  });

  const [confirmBookingMutation] = useMutation(CONFIRM_BOOKING, {
    onCompleted: () => {
      onConfirmation(booking.id);
    },
    onError: (error: ApolloError) =>
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      }),
  });

  const handlePreAuthPayment = async () => {
    setCaptureLater(true)
    setConfirmDirectly(false)
    setLoading(true);
    setAmountToPay(getTotalPayableAmount())
    if (booking) {
      checkBookingConfirmationEligibility({
        variables: {
          bookingId: booking.id
        }
      })
    }
  }

  const calcAdjustableAmount = (creditNotes: ICreditNote[]) => {
    let adjustableAmount: number = 0;
    if (creditNotes.length) {
      creditNotes.forEach((note: ICreditNote) => {
        adjustableAmount += note.availableAmount
      })
    };
    return adjustableAmount;
  }

  const handlePaymentClick = async (amount?: number) => {
    setCaptureLater(false)
    setConfirmDirectly(false)
    if (amount) {
      setAmountToPay(amount)
    } else {
      setAmountToPay(getTotalPayableAmount())
    }
    setLoading(true);
    if (booking) {
      checkBookingConfirmationEligibility({
        variables: {
          bookingId: booking.id
        }
      })
    }
  };

  const handleConfirmBooking = async () => {
    setLoading(true);
    setCaptureLater(false)
    setConfirmDirectly(true);
    setAmountToPay(getTotalPayableAmount())
    if (booking) {
      checkBookingConfirmationEligibility({
        variables: {
          bookingId: booking.id
        }
      })
    }
  };

  const handlePayLater = async () => {
    setLoading(true);
    setCaptureLater(false)
    setConfirmDirectly(true);
    setBookingType('POSTPAID')
    setAmountToPay(getTotalPayableAmount())
    if (booking) {
      checkBookingConfirmationEligibility({
        variables: {
          bookingId: booking.id,
          bookingType: 'POSTPAID'
        }
      })
    }
  }

  useEffect(() => {
    if (creditNotesApplied && adjustableCreditNotes?.length && booking) {
      const adjustableCreditNotesArr = [...adjustableCreditNotes].sort((a,b) => (b.availableAmount - a.availableAmount) > 0 ? 1 : -1)
      const arr: ICreditNotesToAdjust[] = [];
      let payableAmount = booking.paymentDetails.totalPayableAmount;
      let amountPaid = 0;
      let amountRemaining = payableAmount
      for (let i = 0; i < adjustableCreditNotesArr.length; i++) {
        const creditNote: ICreditNote = adjustableCreditNotesArr[i];
        if (amountPaid >= payableAmount) {
          break;
        }
        if (creditNote.availableAmount >= amountRemaining) {
          amountPaid += amountRemaining
          arr.push({
            creditNoteId: creditNote.id,
            amount: amountRemaining
          })
          amountRemaining -= amountRemaining
        } else {
          amountPaid += creditNote.availableAmount
          arr.push({
            creditNoteId: creditNote.id,
            amount: creditNote.availableAmount
          })
          amountRemaining -= creditNote.availableAmount
        }
      }
      setCreditNotesToAdjust(arr);
    }
  }, [creditNotesApplied])

  const handleCheckboxChange = () => {
    if (creditNotesApplied) { // signifies unchecking of checkbox as previous state was true.
      setCreditNotesToAdjust([]) // if checkbox unchecked then creditNotesToAdjust should be empty
    }
    setCreditNotesApplied(!creditNotesApplied)
  }

  if (!booking) {
    return (
      <div style={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  const getMinimumPayableAmount = () => {
    let amount: number = -1;
    const selectedbranch = website.branches.find(branch => branch.id === booking.branchId);
    if (selectedbranch?.minBookingAmountOptIn && selectedbranch.minimumBookingAmount) {
      const chargeType = selectedbranch.minimumBookingAmount.type;
      if (chargeType === MinimumBookingAmountTypes.FIXED) {
        amount = selectedbranch.minimumBookingAmount.value
      } else if (chargeType === MinimumBookingAmountTypes.VARIABLE) {
        amount = Math.round((selectedbranch.minimumBookingAmount.value * booking.paymentDetails.totalPayableAmount) / 100)
      }
      if (paymentGatewayAvailable && collectDeposit) {
        if (booking.depositDetails?.amount && booking.depositDetails.amount > amount) {
          amount = booking.depositDetails.amount
        }
        if (amount > (booking.paymentDetails.totalPayableAmount + (booking.depositDetails?.amount || 0))) {
          amount = -1
        }
      } else {
        if (amount > booking.paymentDetails.totalPayableAmount) {
          amount = -1
        }
      }
    }
    return amount
  }

  const getTotalPayableAmount = () => {
    let amount = booking.paymentDetails.totalPayableAmount;
    if (collectDeposit && booking.depositDetails?.amount) {
      amount += booking.depositDetails.amount
    }
    if (creditNotesApplied && creditNotesToAdjust.length) {
      creditNotesToAdjust.forEach((item) => {
        amount -= item.amount
      })
    }
    if (amount > 0) {
      return amount
    }
    return 0;
  }

  return (
    <Dialog
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      open={open}
      onClose={handleDialogClose}
      aria-labelledby="form-dialog-title"
    >
      <div className={`${styles.paymentDialog} ${styles.mobile}`}>
        <div className={styles.closeDialog}>
          <Typography variant="h2" className="bold">{t("root:proceedToPay")}</Typography>
          <IconButton onClick={() => handleDialogClose()} aria-label="delete">
            <CloseIcon />
          </IconButton>
        </div>
        {!paymentCollection && <>
          <div style={{ marginBottom: 10 }} className="flex padding-left space-between">
            <Grid container xs={12}>
              <Grid item xs={12}>
                <Grid container xs={5}>
                  <Typography variant="h4" style={{ fontWeight: 'initial', display: "inline" }}>{t("root:rentalCharges")}:</Typography>
                  <Typography variant="h3" style={{ marginLeft: "0.5rem", marginTop: "-0.2rem", display: "inline" }} className="bold text-accent">{toCurrency(booking.paymentDetails.totalPayableAmount, currency, locale)}</Typography>
                </Grid>
              </Grid>
              {processingFee > 0 && <Grid item xs={12} style={{marginTop: '3px'}}>
                <Grid container xs={5}>
                  <Typography variant="h4" style={{ fontWeight: 'initial', display: "inline" }}>{"Processing Fee"}:</Typography>
                  <Typography variant="h3" style={{ marginLeft: "0.5rem", marginTop: "-0.2rem", display: "inline" }} className="bold text-accent">{toCurrency(processingFee, currency, locale)}</Typography>
                </Grid>
              </Grid>}
              <Grid container xs={7}>
                {adjustableAmount > 0 && <Grid xs={11}>
                  <Grid container xs={12} style={{marginTop: "-0.9rem", marginLeft: "2rem"}}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={creditNotesApplied}
                            onChange={handleCheckboxChange}
                            value={creditNotesApplied}
                            color="secondary"
                            disabled={loading}
                          />
                        }
                        label={
                          <Typography variant="body1">
                            {`${t("root:useCreditNoteBalance")} : ${toCurrency(adjustableAmount, currency, locale)}`}
                          </Typography>}
                      />
                    </FormGroup>
                  </Grid>
                </Grid>}
              </Grid>
            </Grid>
          </div>
          {collectDeposit && Boolean(booking.depositDetails?.amount) &&
            <div style={{ marginBottom: 15 }} className="flex padding-left space-between">
              <div>
                <Typography variant="h4" style={{ fontWeight: 'initial', display: "inline" }}>{t("root:deposit")}:</Typography>
                <Typography variant="h3" style={{marginLeft: "1rem", display: "inline" }} className="bold text-accent">{toCurrency(booking.depositDetails?.amount || 0, currency, locale)}</Typography>
              </div>
            </div>
          }
          <hr></hr>
        </>}
        <div style={{ marginBottom: 35 }} className="flex padding-left space-between">
          <div>
            <Typography variant="h4" style={{ fontWeight: 'initial', display: "inline"}}>{t("root:totalPayableAmount")}:</Typography>
            <Typography variant="h2" style={{marginLeft: "1rem", display: "inline" }} className="bold text-accent">{toCurrency((amountToPay || getTotalPayableAmount()) + processingFee, currency, locale)}</Typography>
          </div>
        </div>
        {paymentCollection ?
          <StripePayment stripePaymentIntentSecret={stripePaymentIntentSecret} bookingId={booking.id} returnUrl={returnUrl} /> :
          loading ? <CircularProgress /> : getTotalPayableAmount() > 0 ? <>
            {getMinimumPayableAmount() > 0 && !creditNotesApplied ? <div onClick={() => handlePaymentClick(getMinimumPayableAmount())} className={`${styles.payAmoutButton} bold padding-top`}>
              <div>
                <Typography variant="h4" className="bold flex space-between">
                  <span style={{ paddingRight: 40 }}>{t("root:payMinimumAmount")}</span>
                  <span className="text-accent">{toCurrency(getMinimumPayableAmount() + processingFee, currency, locale)}</span>
                </Typography>
                <Typography variant="body1" className="text-grey flex space-between cross-center">
                  <span style={{ paddingRight: 50 }}>{t("root:dueAmountPaidWhilePickup")}</span>
                  <ArrowForwardIcon style={{ marginRight: -4, fontSize: 16 }} />
                </Typography>
              </div>
              <span></span>
            </div> : null}
            {paymentGatewayAvailable && getMinimumPayableAmount() === 0 ? <div onClick={() => handlePreAuthPayment()} className={`${styles.payAmoutButton} bold padding-top`}>
              <div>
                <Typography variant="h4" className="bold flex space-between">
                  <span style={{ paddingRight: 40 }}>{t("root:payOnCollection")}</span>
                  <span className="text-accent">{toCurrency(getTotalPayableAmount() + processingFee, currency, locale)}</span>
                </Typography>
                <Typography variant="body1" className="text-grey flex space-between cross-center">
                  <span style={{ paddingRight: 50 }}>{t("root:cardAuthorizeNowButAmountLater")}</span>
                  <ArrowForwardIcon style={{ marginRight: -4, fontSize: 16 }} />
                </Typography>
              </div>
              <span></span>
            </div> : null}
            {!paymentGatewayAvailable && getMinimumPayableAmount() === 0 ? <div onClick={() => handleConfirmBooking()} className={`${styles.payAmoutButton} bold padding-top`}>
              <div>
                <Typography variant="h4" className="bold flex space-between">
                  <span style={{ paddingRight: 40 }}>{t("root:payOnCollection")}</span>
                  <span className="text-accent">{toCurrency(getTotalPayableAmount(), currency, locale)}</span>
                </Typography>
                <Typography variant="body1" className="text-grey flex space-between cross-center">
                  <span style={{ paddingRight: 50 }}>{t("root:confirmBookingAndPayAtPickup")}</span>
                  <ArrowForwardIcon style={{ marginRight: -4, fontSize: 16 }} />
                </Typography>
              </div>
              <span></span>
            </div> : null}
            {paymentGatewayAvailable && <div onClick={() => handlePaymentClick(getTotalPayableAmount())} className={`${styles.payAmoutButton} bold ${styles.full}`}>
              <div>
                <Typography variant="h4" className="bold flex space-between">
                  <span style={{ paddingRight: 40 }}>{t("root:payFullAmountNow")}</span>
                  <span className="text-accent">{toCurrency(getTotalPayableAmount() + processingFee, currency, locale)}</span>
                </Typography>
                <Typography variant="body1" className="text-grey flex space-between cross-center">
                  <span style={{ paddingRight: 50 }}>{t("root:payFullAmountRightAway")}</span>
                  <ArrowForwardIcon fontSize="small" style={{ marginRight: -4, fontSize: 16 }} />
                </Typography>
                { showTextForConvergePayments
                  ? autoChargeSupported ? 
                  <Grid item xs={12}>
                    <Typography>
                      {t("root:FutureChargesRelatedToOngoingBooking")}
                    </Typography> 
                  </Grid> :
                  [RATE_TYPES.MONTHLY, RATE_TYPES.WEEKLY].includes(booking.rateTypeName) &&
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={saveDetails}
                          onChange={(e: any) => {
                            setSaveDetails(e.target.checked)
                          }}
                          onClick={(e: any) => {
                            e.stopPropagation()
                          }}
                          value={saveDetails}
                          color="primary"
                          name={"agreed"}
                          disabled={sessionLoading}
                        />
                      }
                      label={
                        <Typography variant="body1">
                          {t("root:saveThisCardForFutureInvoicesBooking")}
                        </Typography>
                      }
                    />
                  </FormGroup>
                  : <></>
                }
              </div>
            </div>}
            {(booking.businessCustomer && booking.businessCustomer.creditLimitEnabled && user?.role === UserRole.ADMIN_USER) ? <div onClick={() => handlePayLater()} className={`${styles.payAmoutButton} bold padding-top`}>
          <div>
              <Typography variant="h4" className="bold flex space-between">
                  <span style={{ paddingRight: 40 }}>{t("root:confirmBookingPayLater")}</span>
                  <span className="text-accent">{toCurrency(getTotalPayableAmount() + processingFee, currency, locale)}</span>
              </Typography>
              <Typography variant="body1" className="text-grey flex space-between cross-center">
                  <span style={{ paddingRight: 50 }}>{t("root:creditLimitAssigned")}</span>
                    <ArrowForwardIcon style={{ marginRight: -4, fontSize: 16 }} />
              </Typography>
            </div>
              <span></span>
          </div> : null}
          </> : 
          <>
            <div onClick={() => handlePaymentClick(getTotalPayableAmount())} className={`${styles.payAmoutButton} bold padding-top`}>
              <div>
                <Typography variant="h4" className="bold flex space-between">
                  <span style={{ paddingRight: 40 }}>{t("root:confirmBooking")}</span>
                </Typography>
                <Typography variant="body1" className="text-grey flex space-between cross-center">
                  <span style={{ paddingRight: 50 }}>{t("root:CreditsAgainstBooking")}</span>
                  <ArrowForwardIcon style={{ marginRight: -4, fontSize: 16 }} />
                </Typography>
              </div>
              <span></span>
            </div> 
          </>
        }
      </div>
    </Dialog>
  );
};

export default PaymentDialog;
